import {
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Copy, CopyPlus, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  absorbRecipientText,
  addRecipientChip,
  joinRecipientList,
  looksLikeRecipientToken,
  parseRecipientList,
} from "@/mail/recipientChips";
import { chipDisplayName, chipEmail, chipInitial } from "@/mail/recipientChipMeta";

export type RecipientFieldId = "to" | "cc" | "bcc";

/** Tip 194: chip drag payload between To / Cc / Bcc. */
export const RECIPIENT_DND_MIME = "application/x-atw-hub-recipient";

export type RecipientDragPayload = {
  chip: string;
  from: RecipientFieldId;
};

export function readRecipientDragPayload(data: DataTransfer | null): RecipientDragPayload | null {
  if (!data) return null;
  const raw = data.getData(RECIPIENT_DND_MIME) || data.getData("text/plain");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as RecipientDragPayload;
    if (
      parsed &&
      typeof parsed.chip === "string" &&
      parsed.chip.trim() &&
      (parsed.from === "to" || parsed.from === "cc" || parsed.from === "bcc")
    ) {
      return { chip: parsed.chip.trim(), from: parsed.from };
    }
  } catch {
    /* plain email fallback */
  }
  const chip = raw.trim();
  return chip ? { chip, from: "to" } : null;
}

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Cc / Bcc buttons rendered inside the To row. */
  trailing?: ReactNode;
  /** Field identity for chip drag-and-drop (Tip 194). */
  fieldId?: RecipientFieldId;
  /** Move a chip from another To/Cc/Bcc row into this field. */
  onReceiveChip?: (chip: string, from: RecipientFieldId) => void;
};

/** Long lists (big reply-all threads) collapse to this many chips plus "+N more". */
const COLLAPSED_CHIPS = 3;
const COLLAPSE_WHEN_OVER = 4;

async function writeClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    /* fall through */
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(area);
  }
}

/**
 * Chip-based recipient editor (Gmail / Outlook style).
 * Single-click opens Edit / Copy / Copy all / Remove.
 * Double-click is a quick inline edit.
 * Commit a chip with comma, semicolon, Tab, or Enter.
 */
export function RecipientField({
  label,
  value,
  onChange,
  placeholder = "name@company.com",
  inputRef,
  trailing,
  fieldId,
  onReceiveChip,
}: Props) {
  const reactId = useId();
  const localRef = useRef<HTMLInputElement | null>(null);
  const ref = inputRef ?? localRef;
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [chips, setChips] = useState(() => parseRecipientList(value));
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [menuIndex, setMenuIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const lastEmitted = useRef(value);

  // Sync from parent when value changes externally (Reply ↔ Reply all).
  useEffect(() => {
    const incoming = parseRecipientList(value);
    setChips((prev) => {
      if (joinRecipientList(incoming) === joinRecipientList(prev)) return prev;
      return incoming;
    });
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setExpanded(false);
    }
    setSelected(null);
    setMenuIndex(null);
  }, [value]);

  const collapsed = !expanded && chips.length > COLLAPSE_WHEN_OVER;
  const visibleChips = collapsed ? chips.slice(0, COLLAPSED_CHIPS) : chips;
  const hiddenCount = chips.length - visibleChips.length;

  function expand(focusInput: boolean) {
    setExpanded(true);
    setSelected(null);
    setMenuIndex(null);
    if (focusInput) requestAnimationFrame(() => ref.current?.focus());
  }

  function collapse() {
    commitBuffer();
    setExpanded(false);
    setSelected(null);
    setMenuIndex(null);
  }

  useEffect(() => {
    if (menuIndex == null) return;
    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (fieldRef.current?.contains(target)) return;
      setMenuIndex(null);
      setSelected(null);
    }
    function onKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setMenuIndex(null);
        setSelected(null);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuIndex]);

  useEffect(() => {
    return () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    };
  }, []);

  function emit(nextChips: string[]) {
    const joined = joinRecipientList(nextChips);
    lastEmitted.current = joined;
    setChips(nextChips);
    onChange(joined);
  }

  function commitBuffer() {
    const trimmed = input.trim();
    if (!trimmed || !looksLikeRecipientToken(trimmed)) {
      if (!trimmed) setInput("");
      return;
    }
    emit(addRecipientChip(chips, trimmed));
    setInput("");
    setSelected(null);
    setMenuIndex(null);
  }

  function editChip(index: number) {
    const token = chips[index];
    if (!token) return;
    setMenuIndex(null);
    emit(chips.filter((_, i) => i !== index));
    setInput(token);
    setSelected(null);
    setExpanded(true);
    requestAnimationFrame(() => ref.current?.focus());
  }

  function removeChip(index: number) {
    emit(chips.filter((_, i) => i !== index));
    setSelected(null);
    setMenuIndex(null);
  }

  function openMenu(index: number) {
    setSelected(index);
    setMenuIndex(index);
  }

  function onChipClick(index: number) {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    // Delay so a double-click can cancel and go straight to quick edit.
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      openMenu(index);
    }, 220);
  }

  function onChipDoubleClick(index: number) {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    editChip(index);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "," || event.key === ";") {
      event.preventDefault();
      commitBuffer();
      return;
    }
    if (event.key === "Enter") {
      // Never submit the compose form from a recipient field (Gmail/Outlook).
      event.preventDefault();
      if (input.trim()) commitBuffer();
      return;
    }
    if (event.key === "Tab") {
      if (input.trim()) {
        event.preventDefault();
        commitBuffer();
      }
      return;
    }
    if (event.key === "Backspace" && !input) {
      event.preventDefault();
      if (menuIndex != null) {
        setMenuIndex(null);
        return;
      }
      if (selected != null) {
        removeChip(selected);
        return;
      }
      if (chips.length) setSelected(chips.length - 1);
      return;
    }
    if (event.key === "Delete" && selected != null) {
      event.preventDefault();
      removeChip(selected);
      return;
    }
    if (event.key === "ArrowLeft" && !input && chips.length) {
      event.preventDefault();
      setMenuIndex(null);
      setSelected((prev) => (prev == null ? chips.length - 1 : Math.max(0, prev - 1)));
      return;
    }
    if (event.key === "ArrowRight" && selected != null) {
      event.preventDefault();
      setMenuIndex(null);
      if (selected >= chips.length - 1) setSelected(null);
      else setSelected(selected + 1);
      return;
    }
    if (event.key.length === 1) {
      setSelected(null);
      setMenuIndex(null);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!text || (!/[,\n;]/.test(text) && text.trim().split(/\s+/).length < 2)) {
      return;
    }
    event.preventDefault();
    const forced = absorbRecipientText(chips, `${input}${text.replace(/\n/g, ",")}`, {
      commitTrailing: true,
    });
    emit(forced.chips);
    setInput(forced.input);
    setSelected(null);
    setMenuIndex(null);
  }

  const menuChip = menuIndex != null ? chips[menuIndex] : null;
  const canDrag = Boolean(fieldId);

  function onChipDragStart(event: DragEvent, chip: string) {
    if (!fieldId) return;
    const payload: RecipientDragPayload = { chip, from: fieldId };
    const json = JSON.stringify(payload);
    event.dataTransfer.setData(RECIPIENT_DND_MIME, json);
    event.dataTransfer.setData("text/plain", json);
    event.dataTransfer.effectAllowed = "move";
    setMenuIndex(null);
    setSelected(null);
  }

  function onFieldDragOver(event: DragEvent) {
    if (!onReceiveChip || !fieldId) return;
    const types = [...event.dataTransfer.types];
    if (!types.includes(RECIPIENT_DND_MIME) && !types.includes("text/plain")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropActive(true);
  }

  function onFieldDragLeave(event: DragEvent) {
    if (!fieldRef.current?.contains(event.relatedTarget as Node | null)) {
      setDropActive(false);
    }
  }

  function onFieldDrop(event: DragEvent) {
    setDropActive(false);
    if (!onReceiveChip || !fieldId) return;
    event.preventDefault();
    const payload = readRecipientDragPayload(event.dataTransfer);
    if (!payload || payload.from === fieldId) return;
    onReceiveChip(payload.chip, payload.from);
  }

  return (
    <div className="mail-compose-to-row">
      <div
        ref={fieldRef}
        className={`mail-recipient-field${expanded && chips.length > COLLAPSE_WHEN_OVER ? " is-expanded" : ""}${
          dropActive ? " is-drop-target" : ""
        }`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setMenuIndex(null);
            if (collapsed) expand(true);
            else ref.current?.focus();
          }
        }}
        onDragOver={onFieldDragOver}
        onDragLeave={onFieldDragLeave}
        onDrop={onFieldDrop}
      >
        {visibleChips.map((chip, index) => (
          <span
            key={`${reactId}-${chip}-${index}`}
            className={`mail-recipient-chip-wrap${selected === index || menuIndex === index ? " is-selected" : ""}${
              canDrag ? " is-draggable" : ""
            }`}
            draggable={canDrag}
            onDragStart={(event) => onChipDragStart(event, chip)}
            onDragEnd={() => setDropActive(false)}
          >
            <button
              type="button"
              className="mail-recipient-chip"
              aria-label={`${label} recipient ${chip}`}
              aria-haspopup="menu"
              aria-expanded={menuIndex === index}
              onClick={() => onChipClick(index)}
              onDoubleClick={(event) => {
                event.preventDefault();
                onChipDoubleClick(index);
              }}
            >
              <span className="mail-recipient-chip-avatar" aria-hidden>
                {chipInitial(chip)}
              </span>
              <span className="mail-recipient-chip-label">{chip}</span>
            </button>
            <span className="mail-recipient-chip-actions">
              <button
                type="button"
                className="mail-recipient-chip-action"
                title="Edit"
                aria-label={`Edit ${chip}`}
                onClick={() => editChip(index)}
              >
                <Pencil size={13} aria-hidden />
              </button>
              <button
                type="button"
                className="mail-recipient-chip-action is-danger"
                title="Remove"
                aria-label={`Remove ${chip}`}
                onClick={() => removeChip(index)}
              >
                <X size={14} aria-hidden />
              </button>
            </span>
            {menuIndex === index && menuChip ? (
              <div
                className="mail-recipient-chip-menu"
                role="menu"
                aria-label={`${label} recipient actions`}
              >
                <div className="mail-recipient-chip-menu-head">
                  <span className="mail-recipient-chip-menu-avatar" aria-hidden>
                    {chipInitial(menuChip)}
                  </span>
                  <div className="mail-recipient-chip-menu-meta">
                    <span className="mail-recipient-chip-menu-name">{chipDisplayName(menuChip)}</span>
                    <span className="mail-recipient-chip-menu-email">{chipEmail(menuChip)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="mail-recipient-chip-menu-item"
                  onClick={() => editChip(index)}
                >
                  <Pencil size={16} aria-hidden />
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="mail-recipient-chip-menu-item"
                  onClick={() => {
                    void writeClipboard(chipEmail(menuChip));
                    setMenuIndex(null);
                  }}
                >
                  <Copy size={16} aria-hidden />
                  <span>Copy</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="mail-recipient-chip-menu-item"
                  onClick={() => {
                    void writeClipboard(joinRecipientList(chips));
                    setMenuIndex(null);
                  }}
                >
                  <CopyPlus size={16} aria-hidden />
                  <span>Copy all</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="mail-recipient-chip-menu-item is-danger"
                  onClick={() => removeChip(index)}
                >
                  <Trash2 size={16} aria-hidden />
                  <span>Remove</span>
                </button>
              </div>
            ) : null}
          </span>
        ))}
        {collapsed ? (
          <>
            <button
              type="button"
              className="mail-recipient-more"
              title={`Show all ${chips.length} ${label} recipients`}
              aria-label={`Show ${hiddenCount} more ${label} recipients`}
              onClick={() => expand(false)}
            >
              +{hiddenCount} more
            </button>
            <button
              type="button"
              className="mail-recipient-more mail-recipient-add"
              title={`Add a ${label} recipient`}
              aria-label={`Add a ${label} recipient`}
              onClick={() => expand(true)}
            >
              <Plus size={14} aria-hidden />
              <span>Add</span>
            </button>
          </>
        ) : (
          <input
            ref={ref}
            className="mail-recipient-input"
            type="text"
            aria-label={label}
            value={input}
            placeholder={chips.length ? (expanded ? "Add another" : "") : placeholder}
            autoComplete="off"
            onChange={(event) => {
              const next = event.target.value;
              if (/[,;]/.test(next)) {
                const absorbed = absorbRecipientText(chips, next, {
                  commitTrailing: /[,;]\s*$/.test(next),
                });
                emit(absorbed.chips);
                setInput(absorbed.input);
                setSelected(null);
                setMenuIndex(null);
                return;
              }
              setInput(next);
              setSelected(null);
              setMenuIndex(null);
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={() => commitBuffer()}
          />
        )}
        {expanded && chips.length > COLLAPSE_WHEN_OVER ? (
          <button
            type="button"
            className="mail-recipient-more"
            title={`Show fewer ${label} recipients`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={collapse}
          >
            Show less
          </button>
        ) : null}
      </div>
      {trailing}
    </div>
  );
}
